import config from 'Config/analytics.json';
import { getSheerIdType, getSheerVerificationId } from 'Utils/SheerIdHelpers';
import { SHEER_FIRST_RESPONDER_TYPE, SHEER_MILITARY_TYPE } from 'Utils/Constants';
import { getPageSourceMLP } from './Segment';
import { getDefaultConfig, getStoreState } from 'Utils/Store';

const SEGMENT_PAGE_FLOW_EVENTS = (config && config.segment && config.segment.paymentFlowEvents) || {};
const SEGMENT_PAGE_PURCHASE_FUNNEL = (config && config.segment && config.segment.purchaseFunnelEvents) || {};
const SEGMENT_PAGES = (config && config.segment && config.segment.pages) || {};

const getPathname = () => (window && window.location && window.location.pathname) || '';

/**
 * Return page flow type as outlined in Segment documentation:
 *  page_plan_flow_type -
 *    "new user acquisition", "upgrade plan", "reinstate plan", "redeem gift"
 * @param {Object} - event properties
 * @return {{page_plan_flow_type: string}|{}}
 */
export const getPageFlowType = (name, props) => {
  if (name) {
    const eventName = SEGMENT_PAGE_FLOW_EVENTS[name];
    const {
      appConfig: {
        defaultConfig: { useSegmentPlanFlow },
      },
      subscription: { hasSubscription },
    } = getStoreState();
    if (useSegmentPlanFlow) {
      const defaultPageFlowType = props && props.page_plan_flow_type;
      const currentFlowType = defaultPageFlowType || '';
      let planFlowType = hasSubscription ? 'upgrade plan' : 'new user acquisition';
      if (eventName && eventName.includes('Redeem')) planFlowType = 'redeem gift';
      if ((eventName && eventName.includes('Restart')) || (currentFlowType && currentFlowType.includes('reinstate'))) {
        planFlowType = 'reinstate plan';
      }
      if (eventName && getSheerVerificationId()) {
        const sheerIdType = getSheerIdType();
        if (sheerIdType === SHEER_MILITARY_TYPE) {
          planFlowType = 'military offer';
        } else if (sheerIdType === SHEER_FIRST_RESPONDER_TYPE) {
          planFlowType = 'first responder offer';
        }
      }
      return { page_plan_flow_type: planFlowType };
    }
  }
  return {};
};

/**
 * Return page start source as outlined in Segment documentation:
 *  page_start_source -
 *    "hero", "plan select", "body"
 * @param {Object} - event properties
 * @return {{page_start_source: string}|{}}
 */
export const getPurchaseFunnelSource = (name) => {
  const eventName = SEGMENT_PAGE_PURCHASE_FUNNEL[name];
  if (name && eventName) {
    const mlpSource = getPageSourceMLP();
    return mlpSource ? { page_start_source: mlpSource } : {};
  }
  return {};
};

/**
 * Return second screen properties as outlined in Segment documentation:
 *  page_profile_activation_method - "2nd screen", "main screen" (not applicable for web)
 *  page_activation_code - capture the activation code created for the user
 * @return {{page_activation_code: string, page_profile_activation_method: string }|{}}
 */
export const getSecondScreenProperties = () => {
  const { code, isDeviceActivation } = getStoreState('secondScreen');

  if (code && isDeviceActivation) {
    return {
      page_activation_code: code,
      page_profile_activation_method: '2nd screen',
    };
  }
  return {};
};

export const getSheerIdProperties = () => {
  return { sheer_id: getSheerVerificationId() };
};

/**
 * Return page start source as outlined in Segment documentation.
 * @return {{page_start_source: string}}
 */
export const getPageStartSource = (eventName, props) => {
  if (!eventName) return {};
  const pathName = getPathname();
  const {
    appConfig: { routes },
    tracking: { pageStartSource },
  } = getStoreState();
  const isGiftingHome = pathName.includes(routes && routes.giftingHome);
  let pageStartSourceValue = pageStartSource;

  if (isGiftingHome) {
    pageStartSourceValue = undefined;
  }
  if (props && props.page_start_source) {
    pageStartSourceValue = props.page_start_source;
  }
  return { page_start_source: pageStartSourceValue };
};

/**
 * Return plan properties as outlined in Segment documentation:
 *  page_plan_name - name of plan
 *  page_plan_id - plan ID
 *  page_plan_price - price of plan
 * @return {{page_plan_name: string, page_plan_id: string, page_plan_price }|{}}
 */
export const getPlanProperties = () => {
  const {
    plans: { isPlanSelected, selectedPlan, selectedPlanId, selectedPlanPrice },
    purchaseMode: { isGifting },
    promoCode: { priceToBeCharged },
  } = getStoreState();

  if (isPlanSelected && selectedPlan) {
    const { packageName } = selectedPlan;
    return {
      page_plan_id: isGifting ? undefined : selectedPlanId,
      page_plan_name: isGifting ? undefined : packageName,
      page_plan_price: isGifting ? undefined : priceToBeCharged || selectedPlanPrice,
    };
  }
  return {};
};

export const getPageNameTypeData = (props) => {
  const { pageNamePrefix } = getDefaultConfig();
  const { pageName } = getStoreState('tracking');

  const pageData = { ...SEGMENT_PAGES[props && props.setPageName], ...pageName, ...props };
  if (pageData.page_name) {
    pageData.page_name = pageNamePrefix ? pageNamePrefix + pageData.page_name : pageData.page_name;
  }
  return { page_name: pageData.page_name, page_type: pageData.page_type };
};

/**
 * Return all shared properties
 * @param {string} - event name
 * @param {string} - event properties
 * @return {Object}
 */
export const getSharedProperties = (eventName, props) => ({
  ...getPageNameTypeData(props),
  ...getPageFlowType(eventName, props),
  ...getPageStartSource(eventName, props),
  ...getPlanProperties(),
  ...getPurchaseFunnelSource(eventName),
  ...getSecondScreenProperties(),
  ...getSheerIdProperties(),
});
